import React from "react";
import { Info, Plus, Settings as SettingsIcon, Trash } from "lucide-react";

import DashboardLayout from "@/components/layout/DashboardLayout";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import Typography from "@/components/ui/typography";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-4">
    <Typography size="sh1" as="h2">
      {title}
    </Typography>
    <div className="grid gap-4">{children}</div>
  </section>
);

const DesignSystem = () => {
  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="space-y-2">
          <Typography size="h1">Design System</Typography>
          <Typography size="b2" className="text-muted-foreground">
            Admin-only documentation of our UI components.
          </Typography>
        </div>

        <Section title="Buttons">
          <div className="space-y-6">
            {/* Primary (Solid) */}
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Primary (Solid)
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm">Button</Button>
                <Button>Button</Button>
                <Button size="lg">Button</Button>
                <Button disabled>Button</Button>
              </div>
            </div>

            {/* Accent (Purple) */}
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Accent
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="accent" size="sm">
                  Button
                </Button>
                <Button variant="accent">Button</Button>
                <Button variant="accent" size="lg">
                  Button
                </Button>
                <Button variant="accent" disabled>
                  Button
                </Button>
              </div>
            </div>

            {/* Secondary (Gray fill) */}
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Secondary (Gray fill)
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="secondary" size="sm">
                  Button
                </Button>
                <Button variant="secondary">Button</Button>
                <Button variant="secondary" size="lg">
                  Button
                </Button>
                <Button variant="secondary" disabled>
                  Button
                </Button>
              </div>
            </div>

            {/* Outline (White) */}
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Outline (White)
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" size="sm">
                  Button
                </Button>
                <Button variant="outline">Button</Button>
                <Button variant="outline" size="lg">
                  Button
                </Button>
                <Button variant="outline" disabled>
                  Button
                </Button>
              </div>
            </div>

            {/* Ghost (Text) */}
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Ghost (Text)
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="ghost">Button</Button>
                <Button variant="ghost" disabled>
                  Button
                </Button>
              </div>
            </div>

            {/* Destructive */}
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Destructive
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="destructive">Delete</Button>
                <Button variant="destructive" disabled>
                  Delete
                </Button>
              </div>
            </div>

            {/* Icon Buttons */}
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Icon buttons
              </Typography>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="icon">
                  <Plus />
                </Button>
                <Button size="icon" variant="outline">
                  <SettingsIcon />
                </Button>
                <Button size="icon" variant="secondary">
                  <Trash />
                </Button>
                <Button size="icon" variant="ghost" disabled>
                  <Plus />
                </Button>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Typography">
          <div className="grid gap-4">
            <div className="grid gap-1">
              <Typography size="h1">
                H1 The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography size="h2">
                H2 The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography size="h3">
                H3 The quick brown fox jumps over the lazy dog.
              </Typography>
            </div>
            <div className="grid gap-1">
              <Typography size="sh1">
                SH1 The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography size="sh2">
                SH2 The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography size="sh3">
                SH3 The quick brown fox jumps over the lazy dog.
              </Typography>
            </div>
            <div className="grid gap-1">
              <Typography size="b1">
                B1 The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography size="b2">
                B2 The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography size="b3">
                B3 The quick brown fox jumps over the lazy dog.
              </Typography>
            </div>
            <div className="grid gap-1">
              <Typography size="l1">L1 Label</Typography>
              <Typography size="l2">L2 Label</Typography>
              <Typography size="l3">L3 Label</Typography>
              <Typography size="l4">L4 Label</Typography>
            </div>
          </div>
        </Section>

        <Section title="Badges">
          <div className="space-y-6">
            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Variants (Purple)
              </Typography>
              <div className="flex flex-wrap gap-3">
                <Badge color="purple" variant="full">
                  Beta
                </Badge>
                <Badge color="purple" variant="opaque">
                  Beta
                </Badge>
                <Badge color="purple" variant="outline">
                  Beta
                </Badge>
              </div>
            </div>

            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                Colors (Full)
              </Typography>
              <div className="flex flex-wrap gap-3">
                <Badge color="purple" variant="full">
                  Purple
                </Badge>
                <Badge color="blue" variant="full">
                  Blue
                </Badge>
                <Badge color="green" variant="full">
                  Green
                </Badge>
                <Badge color="yellow" variant="full">
                  Yellow
                </Badge>
                <Badge color="red" variant="full">
                  Red
                </Badge>
                <Badge color="grey" variant="full">
                  Grey
                </Badge>
                <Badge color="lightGrey" variant="full">
                  Light Grey
                </Badge>
              </div>
            </div>

            <div className="grid gap-2">
              <Typography size="l2" className="text-muted-foreground">
                With icons
              </Typography>
              <div className="flex flex-wrap gap-3">
                <Badge
                  color="purple"
                  variant="full"
                  leftIcon={<Info className="h-4 w-4" />}
                >
                  Info
                </Badge>
                <Badge
                  color="yellow"
                  variant="outline"
                  rightIcon={<Info className="h-4 w-4" />}
                >
                  Tip
                </Badge>
                <Badge
                  color="blue"
                  variant="opaque"
                  leftIcon={<Info className="h-4 w-4" />}
                  rightIcon={<Info className="h-4 w-4" />}
                >
                  Status
                </Badge>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Inputs">
          <div className="grid gap-6 max-w-xl">
            {/* Default */}
            <div className="grid gap-1">
              <Label htmlFor="input-default">Label</Label>
              <Input id="input-default" placeholder="Placeholder" />
              <p className="text-xs text-sds-neutral-60">Helper text</p>
            </div>

            {/* Focus (preview by forcing focus styles) */}
            <div className="grid gap-1">
              <Label htmlFor="input-focus">Label</Label>
              <Input
                id="input-focus"
                className="border-sds-purple-100"
                placeholder="Placeholder"
              />
              <p className="text-xs text-sds-neutral-60">Helper text</p>
            </div>

            {/* Invalid */}
            <div className="grid gap-1">
              <Label htmlFor="input-error">Label</Label>
              <Input id="input-error" aria-invalid placeholder="Placeholder" />
              <p className="text-xs text-destructive">Helper text</p>
            </div>

            {/* Disabled */}
            <div className="grid gap-1">
              <Label htmlFor="input-disabled" className="text-sds-neutral-60">
                Label
              </Label>
              <Input id="input-disabled" disabled placeholder="Placeholder" />
              <p className="text-xs text-sds-neutral-40">Helper text</p>
            </div>
          </div>
        </Section>

        <Section title="Select">
          <div className="grid gap-6 max-w-xl">
            {/* Default */}
            <div className="grid gap-1">
              <Label>Label</Label>
              <Select>
                <SelectTrigger className="w-[280px]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="us">United States</SelectItem>
                  <SelectItem value="ca">Canada</SelectItem>
                  <SelectItem value="gb">United Kingdom</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-sds-neutral-60">Helper text</p>
            </div>

            {/* Invalid */}
            <div className="grid gap-1">
              <Label>Label</Label>
              <Select>
                <SelectTrigger aria-invalid className="w-[280px]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-destructive">Helper text</p>
            </div>

            {/* Disabled */}
            <div className="grid gap-1">
              <Label className="text-sds-neutral-60">Label</Label>
              <Select disabled>
                <SelectTrigger className="w-[280px]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
              </Select>
              <p className="text-xs text-sds-neutral-40">Helper text</p>
            </div>
          </div>
        </Section>

        <Section title="Textarea">
          <div className="grid gap-6 max-w-xl">
            {/* Default */}
            <div className="grid gap-1">
              <Label htmlFor="ta-default">Label</Label>
              <Textarea id="ta-default" placeholder="Type your message..." />
              <p className="text-xs text-sds-neutral-60">Helper text</p>
            </div>

            {/* Invalid */}
            <div className="grid gap-1">
              <Label htmlFor="ta-error">Label</Label>
              <Textarea
                id="ta-error"
                aria-invalid
                placeholder="Type your message..."
              />
              <p className="text-xs text-destructive">Helper text</p>
            </div>

            {/* Disabled */}
            <div className="grid gap-1">
              <Label htmlFor="ta-disabled" className="text-sds-neutral-60">
                Label
              </Label>
              <Textarea
                id="ta-disabled"
                disabled
                placeholder="Type your message..."
              />
              <p className="text-xs text-sds-neutral-40">Helper text</p>
            </div>
          </div>
        </Section>

        <Section title="Table">
          <div className="max-w-3xl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>Jane Cooper</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>$1,200</TableCell>
                  <TableCell>Aug 18, 2025</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Wade Warren</TableCell>
                  <TableCell>Unpaid</TableCell>
                  <TableCell>$350</TableCell>
                  <TableCell>Aug 12, 2025</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Esther Howard</TableCell>
                  <TableCell>Draft</TableCell>
                  <TableCell>$980</TableCell>
                  <TableCell>Aug 2, 2025</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Section>

        <Section title="Checkboxes">
          <div className="grid gap-6 max-w-xl">
            <div className="flex items-center gap-3">
              <Checkbox id="cb-1" />
              <Label htmlFor="cb-1">Placeholder</Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox id="cb-2" defaultChecked />
              <Label htmlFor="cb-2">Checked</Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox id="cb-3" disabled />
              <Label htmlFor="cb-3" className="text-sds-neutral-60">
                Disabled
              </Label>
            </div>
          </div>
        </Section>

        <Section title="Cards">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  <Typography size="sh1" as="span">
                    Card Title
                  </Typography>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Typography size="b2" className="text-muted-foreground">
                  Use cards to group related content.
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>
                  <Typography size="sh1" as="span">
                    With Actions
                  </Typography>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between">
                <Typography>Interactive content</Typography>
                <Button size="sm">Action</Button>
              </CardContent>
            </Card>
          </div>
        </Section>

        <Section title="Tabs">
          <div className="max-w-xl">
            <Tabs defaultValue="account">
              <TabsList>
                <TabsTrigger value="account">Account</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
              </TabsList>
              <TabsContent value="account" className="pt-4">
                <Typography size="b2">
                  Make changes to your account here.
                </Typography>
              </TabsContent>
              <TabsContent value="password" className="pt-4">
                <Typography size="b2">Change your password here.</Typography>
              </TabsContent>
            </Tabs>
          </div>
        </Section>

        <Separator />

        <Section title="Colors">
          <div className="space-y-8">
            {/* Core */}
            <div className="space-y-4">
              <Typography size="sh2" as="h3">
                Core
              </Typography>
              {/* Primary (Black / White) */}
              <div>
                <Typography
                  size="l2"
                  className="mb-2 font-semibold text-muted-foreground"
                >
                  Primary
                </Typography>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                  {[
                    ["Primary Black", "bg-sds-text-black"],
                    ["Primary White", "bg-sds-text-white"],
                  ].map(([label, cls]) => (
                    <div key={label as string} className="space-y-2">
                      <div
                        className={`h-12 w-full rounded-md border ${
                          cls as string
                        }`}
                      ></div>
                      <Typography size="l4" className="text-muted-foreground">
                        {label}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
              {/* Accent 1 (Purple Scale) */}
              <div>
                <Typography
                  size="l2"
                  className="mb-2 font-semibold text-muted-foreground"
                >
                  Accent 1
                </Typography>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[
                    ["Purple/100", "bg-sds-purple-100"],
                    ["Purple/80", "bg-sds-purple-80"],
                    ["Purple/60", "bg-sds-purple-60"],
                    ["Purple/40", "bg-sds-purple-40"],
                    ["Purple/20", "bg-sds-purple-20"],
                  ].map(([label, cls]) => (
                    <div key={label as string} className="space-y-2">
                      <div
                        className={`h-12 w-full rounded-md border ${
                          cls as string
                        }`}
                      ></div>
                      <Typography size="l4" className="text-muted-foreground">
                        {label}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Neutral */}
            <div className="space-y-4">
              <Typography size="sh2" as="h3">
                Neutral
              </Typography>
              {/* Primary text colour */}
              <div>
                <Typography
                  size="l2"
                  className="mb-2 font-semibold text-muted-foreground"
                >
                  Primary text colour
                </Typography>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                  {[
                    ["Text Black", "bg-sds-text-black"],
                    ["Text White", "bg-sds-text-white"],
                  ].map(([label, cls]) => (
                    <div key={label as string} className="space-y-2">
                      <div
                        className={`h-12 w-full rounded-md border ${
                          cls as string
                        }`}
                      ></div>
                      <Typography size="l4" className="text-muted-foreground">
                        {label}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
              {/* Background */}
              <div>
                <Typography
                  size="l2"
                  className="mb-2 font-semibold text-muted-foreground"
                >
                  Background
                </Typography>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                  {[
                    ["Background/Dark", "bg-sds-background-dark"],
                    ["Background/Light", "bg-sds-background-light"],
                  ].map(([label, cls]) => (
                    <div key={label as string} className="space-y-2">
                      <div
                        className={`h-12 w-full rounded-md border ${
                          cls as string
                        }`}
                      ></div>
                      <Typography size="l4" className="text-muted-foreground">
                        {label}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
              {/* Neutrals scale */}
              <div>
                <Typography
                  size="l2"
                  className="mb-2 font-semibold text-muted-foreground"
                >
                  Neutrals
                </Typography>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {[
                    ["Neutral/100", "bg-sds-neutral-100"],
                    ["Neutral/80", "bg-sds-neutral-80"],
                    ["Neutral/60", "bg-sds-neutral-60"],
                    ["Neutral/40", "bg-sds-neutral-40"],
                    ["Neutral/20", "bg-sds-neutral-20"],
                    ["Neutral/00", "bg-sds-neutral-00"],
                  ].map(([label, cls]) => (
                    <div key={label as string} className="space-y-2">
                      <div
                        className={`h-12 w-full rounded-md border ${
                          cls as string
                        }`}
                      ></div>
                      <Typography size="l4" className="text-muted-foreground">
                        {label}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Semantics */}
            <div className="space-y-3">
              <Typography size="sh2" as="h3">
                Semantics
              </Typography>
              <div className="space-y-4">
                <div>
                  <Typography
                    size="l2"
                    className="mb-2 font-semibold text-muted-foreground"
                  >
                    Info
                  </Typography>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {[
                      ["Blue/100", "bg-sds-blue-100"],
                      ["Blue/80", "bg-sds-blue-80"],
                      ["Blue/60", "bg-sds-blue-60"],
                      ["Blue/40", "bg-sds-blue-40"],
                      ["Blue/20", "bg-sds-blue-20"],
                    ].map(([label, cls]) => (
                      <div key={label as string} className="space-y-2">
                        <div
                          className={`h-12 w-full rounded-md border ${
                            cls as string
                          }`}
                        ></div>
                        <Typography size="l4" className="text-muted-foreground">
                          {label}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Typography
                    size="l2"
                    className="mb-2 font-semibold text-muted-foreground"
                  >
                    Success
                  </Typography>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {[
                      ["Green/100", "bg-sds-green-100"],
                      ["Green/80", "bg-sds-green-80"],
                      ["Green/60", "bg-sds-green-60"],
                      ["Green/40", "bg-sds-green-40"],
                      ["Green/20", "bg-sds-green-20"],
                    ].map(([label, cls]) => (
                      <div key={label as string} className="space-y-2">
                        <div
                          className={`h-12 w-full rounded-md border ${
                            cls as string
                          }`}
                        ></div>
                        <Typography size="l4" className="text-muted-foreground">
                          {label}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Typography
                    size="l2"
                    className="mb-2 font-semibold text-muted-foreground"
                  >
                    Warning
                  </Typography>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {[
                      ["Yellow/100", "bg-sds-yellow-100"],
                      ["Yellow/80", "bg-sds-yellow-80"],
                      ["Yellow/60", "bg-sds-yellow-60"],
                      ["Yellow/40", "bg-sds-yellow-40"],
                      ["Yellow/20", "bg-sds-yellow-20"],
                    ].map(([label, cls]) => (
                      <div key={label as string} className="space-y-2">
                        <div
                          className={`h-12 w-full rounded-md border ${
                            cls as string
                          }`}
                        ></div>
                        <Typography size="l4" className="text-muted-foreground">
                          {label}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Typography
                    size="l2"
                    className="mb-2 font-semibold text-muted-foreground"
                  >
                    Error
                  </Typography>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {[
                      ["Red/100", "bg-sds-red-100"],
                      ["Red/80", "bg-sds-red-80"],
                      ["Red/60", "bg-sds-red-60"],
                      ["Red/40", "bg-sds-red-40"],
                      ["Red/20", "bg-sds-red-20"],
                    ].map(([label, cls]) => (
                      <div key={label as string} className="space-y-2">
                        <div
                          className={`h-12 w-full rounded-md border ${
                            cls as string
                          }`}
                        ></div>
                        <Typography size="l4" className="text-muted-foreground">
                          {label}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>
    </DashboardLayout>
  );
};

export default DesignSystem;
